import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../hooks/useMediaQuery';
import RelationshipCard, { RelationshipCardList, joinLocation } from './RelationshipCard';

// Shared by MyShows.js and PublicProfile.js — Shows by Year, Venues Visited,
// Artists Watched, Top Cities, and Shows Map are identical between "my own"
// and "someone else's" show data, so they live here once instead of being
// duplicated across both pages.
//
// My Shows Redesign (2026-09-22): VenuesVisitedTab and ArtistsWatchedTab take
// an optional paginationStyle prop ('standard' default | 'jump'). MyShows.js
// passes 'jump' (FestivalDetail.js's dropdown jump-to-page control, exported
// here as JumpPagination); PublicProfile.js passes nothing and is unchanged.

const PAGE_SIZE = 25;

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
export const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();

// ── Pagination ────────────────────────────────────────────────────────────────
export function Pagination({ currentPage, totalItems, onPageChange }) {
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    if (totalPages <= 1) return null;
    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '20px', fontSize: '14px' }}>
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={tabStyles.pgBtn}>←</button>
            <span style={{ color: '#aaa' }}>Page {currentPage} of {totalPages}</span>
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={tabStyles.pgBtn}>→</button>
        </div>
    );
}

// ── Jump-to-page Pagination (My Shows Redesign, 2026-09-22) ─────────────────
// Copy of FestivalDetail.js's dropdown "jump to page" control, same styles.
export function JumpPagination({ currentPage, totalItems, onPageChange, pageSize = PAGE_SIZE }) {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    if (totalPages <= 1) return null;
    return (
        <div style={tabStyles.pageJumpWrap}>
            <span style={tabStyles.pageJumpLabel}>Page:</span>
            <select
                value={currentPage}
                onChange={e => onPageChange(Number(e.target.value))}
                style={tabStyles.pageJumpSelect}
            >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <option key={p} value={p}>{p}</option>
                ))}
            </select>
            <span style={tabStyles.pageJumpLabel}>of {totalPages}</span>
        </div>
    );
}

export { PAGE_SIZE };

// ── Shows by Year Tab ─────────────────────────────────────────────────────────
export function ShowsByYearTab({ shows }) {
    if (!shows || shows.length === 0) return <div style={tabStyles.emptyTab}>No shows recorded yet.</div>;

    const years = shows.filter(s => s.event_date).map(s => new Date(s.event_date).getUTCFullYear());
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const counts = {};
    years.forEach(y => { counts[y] = (counts[y] || 0) + 1; });
    const data = [];
    for (let y = minYear; y <= maxYear; y++) data.push({ year: y, count: counts[y] || 0 });

    const maxCount = Math.max(...data.map(d => d.count), 1);
    const barMaxHeight = 200;
    const step = maxCount <= 5 ? 1 : maxCount <= 10 ? 2 : maxCount <= 20 ? 5 : 10;
    const gridLines = [];
    for (let v = 0; v <= maxCount; v += step) gridLines.push(v);
    if (gridLines[gridLines.length - 1] < maxCount) gridLines.push(maxCount);

    const chartWidth = Math.max(data.length * 52, 400);
    const yAxisWidth = 36;
    const chartPadTop = 16;
    const chartPadBottom = 40;
    const totalHeight = barMaxHeight + chartPadTop + chartPadBottom;

    return (
        <div>
            <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                <div style={{ display: 'flex', minWidth: chartWidth + yAxisWidth + 'px' }}>
                    <div style={{ width: yAxisWidth + 'px', flexShrink: 0, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => {
                            const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight;
                            return <div key={v} style={{ position: 'absolute', top: top + 'px', right: '6px', transform: 'translateY(-50%)', fontSize: '11px', color: '#666', whiteSpace: 'nowrap' }}>{v}</div>;
                        })}
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => {
                            const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight;
                            return <div key={v} style={{ position: 'absolute', top: top + 'px', left: 0, right: 0, borderTop: `1px solid ${v === 0 ? '#444' : '#2a2a2a'}`, zIndex: 0 }} />;
                        })}
                        <div style={{ display: 'flex', alignItems: 'flex-end', height: barMaxHeight + 'px', marginTop: chartPadTop + 'px', gap: '4px', paddingLeft: '8px', paddingRight: '8px', position: 'relative', zIndex: 1 }}>
                            {data.map(({ year, count }) => {
                                const barH = count === 0 ? 0 : Math.max((count / maxCount) * barMaxHeight, 4);
                                return (
                                    <div key={year} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }} title={`${year}: ${count} show${count !== 1 ? 's' : ''}`}>
                                        {count > 0 && <div style={{ fontSize: '11px', color: '#bb86fc', fontWeight: 'bold', opacity: 0.85 }}>{count}</div>}
                                        <div style={{ width: '100%', maxWidth: '44px', height: barH + 'px', backgroundColor: count === 0 ? 'transparent' : '#bb86fc', borderRadius: '3px 3px 0 0' }} />
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', paddingLeft: '8px', paddingRight: '8px', gap: '4px', marginTop: '6px' }}>
                            {data.map(({ year, count }) => (
                                <div key={year} style={{ flex: 1, textAlign: 'center', fontSize: '11px', color: count === 0 ? '#444' : '#888', whiteSpace: 'nowrap', overflow: 'hidden' }}>{year}</div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '32px', fontSize: '13px', color: '#888' }}>
                <span>Total Years Active: <strong style={{ color: '#e0e0e0' }}>{data.filter(d => d.count > 0).length}</strong></span>
                <span>Peak Year: <strong style={{ color: '#bb86fc' }}>{data.reduce((best, d) => d.count > best.count ? d : best, data[0]).year} ({data.reduce((best, d) => d.count > best.count ? d : best, data[0]).count} shows)</strong></span>
            </div>
        </div>
    );
}

// ── Venues Visited Tab ────────────────────────────────────────────────────────
export function VenuesVisitedTab({ venues, paginationStyle = 'standard' }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const isMobile = useIsMobile();

    if (!venues || venues.length === 0) return <div style={tabStyles.emptyTab}>No venues recorded yet.</div>;

    const q = normalizeStr(search);
    const filtered = search
        ? venues.filter(v => [v.name, v.city, v.state, v.country].some(val => val && normalizeStr(val).includes(q)))
        : venues;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input type="text" placeholder="Search by venue, city, state, country..." value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} style={tabStyles.tabSearch} />
            {filtered.length === 0 ? <div style={tabStyles.emptyTab}>No venues match your search.</div> : (
                <>
                    {/* Mobile (2026-10-28): one card per row instead of the wide table */}
                    {isMobile ? (
                        <RelationshipCardList>
                            {paginated.map(v => (
                                <RelationshipCard
                                    key={v.id}
                                    title={v.name}
                                    titleTo={`/venues/${v.slug || v.id}`}
                                    lines={[joinLocation(v.city, v.state, v.country)]}
                                    counts={[{ value: v.times_played, label: 'Times Visited' }]}
                                />
                            ))}
                        </RelationshipCardList>
                    ) : (
                        <div style={tabStyles.tableContainer}>
                            <table style={tabStyles.table}>
                                <thead>
                                    <tr>
                                        <th style={tabStyles.th}>Venue</th>
                                        <th style={tabStyles.th}>City</th>
                                        <th style={tabStyles.th}>State/Region</th>
                                        <th style={tabStyles.th}>Country</th>
                                        <th style={tabStyles.th}>Times Visited</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((v, idx) => (
                                        <tr key={v.id} style={idx % 2 === 0 ? tabStyles.trEven : tabStyles.trOdd}>
                                            <td style={tabStyles.td}><Link to={`/venues/${v.slug || v.id}`} style={tabStyles.tableLink}>{v.name}</Link></td>
                                            <td style={tabStyles.td}>{v.city || '—'}</td>
                                            <td style={tabStyles.td}>{v.state || '—'}</td>
                                            <td style={tabStyles.td}>{v.country || '—'}</td>
                                            <td style={tabStyles.td}><span style={tabStyles.showCount}>{v.times_played}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {paginationStyle === 'jump'
                        ? <JumpPagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                        : <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />}
                </>
            )}
        </div>
    );
}

// ── Artists Watched Tab ───────────────────────────────────────────────────────
export function ArtistsWatchedTab({ artists, paginationStyle = 'standard' }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const isMobile = useIsMobile();

    if (!artists || artists.length === 0) return <div style={tabStyles.emptyTab}>No artists marked as "Caught This" yet.</div>;

    const q = normalizeStr(search);
    const filtered = search
        ? artists.filter(a => [a.name, a.city, a.state, a.country].some(val => val && normalizeStr(val).includes(q)))
        : artists;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input type="text" placeholder="Search by artist, city, state, country..." value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} style={tabStyles.tabSearch} />
            {filtered.length === 0 ? <div style={tabStyles.emptyTab}>No artists match your search.</div> : (
                <>
                    {/* Mobile (2026-10-28): one card per row instead of the wide table */}
                    {isMobile ? (
                        <RelationshipCardList>
                            {paginated.map(a => (
                                <RelationshipCard
                                    key={a.id}
                                    title={a.name}
                                    titleTo={`/artists/${a.slug || a.id}`}
                                    lines={[joinLocation(a.city, a.state, a.country)]}
                                    counts={[{ value: `${a.times_watched}x`, label: 'Times Watched' }]}
                                />
                            ))}
                        </RelationshipCardList>
                    ) : (
                        <div style={tabStyles.tableContainer}>
                            <table style={tabStyles.table}>
                                <thead>
                                    <tr>
                                        <th style={tabStyles.th}>Artist</th>
                                        <th style={tabStyles.th}>City</th>
                                        <th style={tabStyles.th}>State/Region</th>
                                        <th style={tabStyles.th}>Country</th>
                                        <th style={tabStyles.th}>Times Watched</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((a, idx) => (
                                        <tr key={a.id} style={idx % 2 === 0 ? tabStyles.trEven : tabStyles.trOdd}>
                                            <td style={tabStyles.td}><Link to={`/artists/${a.slug || a.id}`} style={tabStyles.tableLink}>{a.name}</Link></td>
                                            <td style={tabStyles.td}>{a.city || '—'}</td>
                                            <td style={tabStyles.td}>{a.state || '—'}</td>
                                            <td style={tabStyles.td}>{a.country || '—'}</td>
                                            <td style={tabStyles.td}><span style={tabStyles.showCount}>{a.times_watched}x</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {paginationStyle === 'jump'
                        ? <JumpPagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                        : <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />}
                </>
            )}
        </div>
    );
}

// ── Top Cities Tab ────────────────────────────────────────────────────────────
export function TopCitiesTab({ geography }) {
    if (!geography || geography.length === 0) return <div style={tabStyles.emptyTab}>No geographic data available.</div>;

    // Hierarchical rollup: country > state > city > venue
    const rollup = {};
    geography.forEach(row => {
        const country = row.country || 'Unknown';
        const state = row.state || '—';
        const city = row.city || '—';
        const count = parseInt(row.show_count) || 0;
        if (!rollup[country]) rollup[country] = { total: 0, states: {} };
        rollup[country].total += count;
        if (!rollup[country].states[state]) rollup[country].states[state] = { total: 0, cities: {} };
        rollup[country].states[state].total += count;
        if (!rollup[country].states[state].cities[city]) rollup[country].states[state].cities[city] = { total: 0, venues: {} };
        rollup[country].states[state].cities[city].total += count;
        const venueKey = `${row.venue_id}__${row.venue_name}`;
        if (!rollup[country].states[state].cities[city].venues[venueKey]) {
            rollup[country].states[state].cities[city].venues[venueKey] = { id: row.venue_id, name: row.venue_name, slug: row.venue_slug, count: 0 };
        }
        rollup[country].states[state].cities[city].venues[venueKey].count += count;
    });

    const allRows = [];
    Object.entries(rollup).sort((a, b) => b[1].total - a[1].total).forEach(([country, cData]) => {
        let countryFirst = true;
        Object.entries(cData.states).sort((a, b) => b[1].total - a[1].total).forEach(([state, sData]) => {
            let stateFirst = true;
            Object.entries(sData.cities).sort((a, b) => b[1].total - a[1].total).forEach(([city, cityData]) => {
                let cityFirst = true;
                Object.values(cityData.venues).sort((a, b) => b.count - a.count).forEach(venue => {
                    allRows.push({ countryFirst, stateFirst, cityFirst, country, state, city, cTotal: cData.total, sTotal: sData.total, cityTotal: cityData.total, venue });
                    countryFirst = false; stateFirst = false; cityFirst = false;
                });
            });
        });
    });

    return (
        <div style={tabStyles.tableContainer}>
            <table style={tabStyles.table}>
                <thead>
                    <tr>
                        <th style={tabStyles.th}>Country</th>
                        <th style={tabStyles.th}>State/Region</th>
                        <th style={tabStyles.th}>City</th>
                        <th style={tabStyles.th}>Venue</th>
                        <th style={{ ...tabStyles.th, textAlign: 'right' }}>Shows</th>
                    </tr>
                </thead>
                <tbody>
                    {allRows.map((row, idx) => (
                        <tr key={idx} style={idx % 2 === 0 ? tabStyles.trEven : tabStyles.trOdd}>
                            <td style={{ ...tabStyles.td, fontWeight: row.countryFirst ? 'bold' : 'normal', color: row.countryFirst ? '#e0e0e0' : 'transparent' }}>
                                {row.countryFirst ? <span>{row.country} <span style={tabStyles.geoSubCount}>({row.cTotal})</span></span> : '\u00a0'}
                            </td>
                            <td style={{ ...tabStyles.td, paddingLeft: '24px', color: row.stateFirst ? '#ccc' : 'transparent' }}>
                                {row.stateFirst ? <span>{row.state} <span style={tabStyles.geoSubCount}>({row.sTotal})</span></span> : '\u00a0'}
                            </td>
                            <td style={{ ...tabStyles.td, paddingLeft: '24px', color: row.cityFirst ? '#bbb' : 'transparent' }}>
                                {row.cityFirst ? <span>{row.city} <span style={tabStyles.geoSubCount}>({row.cityTotal})</span></span> : '\u00a0'}
                            </td>
                            <td style={{ ...tabStyles.td, paddingLeft: '32px' }}>
                                {row.venue.id
                                    ? <Link to={`/venues/${row.venue.slug || row.venue.id}`} style={tabStyles.tableLink}>{row.venue.name}</Link>
                                    : <span style={{ color: '#aaa' }}>{row.venue.name}</span>}
                            </td>
                            <td style={{ ...tabStyles.td, textAlign: 'right' }}>
                                <span style={tabStyles.showCount}>{row.venue.count}</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ── Shows Map Tab ─────────────────────────────────────────────────────────────
export function ShowsMapTab({ showMap }) {
    const mapRef = React.useRef(null);
    const mapInstanceRef = React.useRef(null);
    const [leafletReady, setLeafletReady] = React.useState(!!window.L);

    React.useEffect(() => {
        if (window.L) { setLeafletReady(true); return; }
        if (!document.getElementById('leaflet-css')) {
            const link = document.createElement('link');
            link.id = 'leaflet-css'; link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);
        }
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => setLeafletReady(true);
        document.head.appendChild(script);
    }, []);

    React.useEffect(() => {
        if (!leafletReady || !mapRef.current || !showMap || showMap.length === 0) return;
        if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
        const L = window.L;
        const map = L.map(mapRef.current, { zoomControl: true });
        mapInstanceRef.current = map;
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors', maxZoom: 18,
        }).addTo(map);
        const markerIcon = L.divIcon({
            className: '',
            html: `<div style="width:12px;height:12px;background:#bb86fc;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.5);"></div>`,
            iconSize: [12, 12], iconAnchor: [6, 6], popupAnchor: [0, -8],
        });
        const bounds = [];
        showMap.forEach(venue => {
            const lat = parseFloat(venue.lat);
            const lng = parseFloat(venue.lng);
            if (isNaN(lat) || isNaN(lng)) return;
            bounds.push([lat, lng]);
            const dates = venue.show_dates
                ? venue.show_dates.split(',').map(d => new Date(d.trim()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }))
                : [];
            const locationStr = [venue.city, venue.state, venue.country].filter(Boolean).join(', ');
            const showCount = parseInt(venue.show_count);
            const popupHtml = `
                <div style="font-family:sans-serif;min-width:180px;max-width:260px;">
                    <div style="font-weight:bold;font-size:14px;margin-bottom:4px;">
                        <a href="/venues/${venue.venue_slug || venue.venue_id}" style="color:#bb86fc;text-decoration:none;">${venue.venue_name} ↗</a>
                    </div>
                    ${locationStr ? `<div style="font-size:12px;color:#888;margin-bottom:6px;">${locationStr}</div>` : ''}
                    <div style="font-size:12px;color:#aaa;margin-bottom:${dates.length ? '6px' : '0'};">
                        <strong style="color:#e0e0e0;">${showCount}</strong> show${showCount !== 1 ? 's' : ''}
                    </div>
                    ${dates.length > 0 ? `<div style="font-size:11px;color:#999;line-height:1.6;">${dates.slice(0, 10).join('<br/>')}${dates.length > 10 ? `<br/><em>+${dates.length - 10} more</em>` : ''}</div>` : ''}
                </div>`;
            L.marker([lat, lng], { icon: markerIcon }).bindPopup(popupHtml, { maxWidth: 280 }).addTo(map);
        });
        if (bounds.length === 1) map.setView(bounds[0], 12);
        else if (bounds.length > 1) map.fitBounds(bounds, { padding: [40, 40] });
        return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
    }, [leafletReady, showMap]);

    if (!showMap || showMap.length === 0) return <div style={tabStyles.emptyTab}>No geocoded venues available yet.</div>;

    return (
        <div>
            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#888' }}>
                {showMap.length} venue{showMap.length !== 1 ? 's' : ''} with location data · Click a pin for details
            </div>
            {!leafletReady
                ? <div style={tabStyles.emptyTab}>Loading map...</div>
                : <div ref={mapRef} style={{ height: '480px', borderRadius: '8px', border: '1px solid #333', overflow: 'hidden' }} />
            }
        </div>
    );
}

// ── Shared styles used by the tab components above ─────────────────────────────
export const tabStyles = {
    pgBtn: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', color: '#e0e0e0', padding: '5px 12px', cursor: 'pointer', fontSize: '14px' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '10px 16px', color: '#e0e0e0', verticalAlign: 'middle' },
    trEven: { backgroundColor: 'transparent' },
    trOdd: { backgroundColor: '#1e1e1e' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    showCount: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    geoSubCount: { fontSize: '11px', color: '#666', fontWeight: 'normal' },
    emptyTab: { color: '#666', fontSize: '14px', fontStyle: 'italic', padding: '24px 0' },
    // JumpPagination — identical values to FestivalDetail.js
    pageJumpWrap: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' },
    pageJumpLabel: { fontSize: '13px', color: '#aaa', fontWeight: '600' },
    pageJumpSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 10px', fontSize: '14px', color: '#e0e0e0', cursor: 'pointer', fontWeight: 'bold', width: '64px', flexShrink: 0, textAlign: 'center', textAlignLast: 'center' },
};
